import _ from "lodash";
import siteConfig from "site-config";
import { Observable } from "rxjs";
import { progressEpic } from "epics/base-epic";
import { APIError } from "lib/errors";
import { formatRatesStatus, getSplitKeyLabels } from "helpers/shipping-rates";
import { SHIPPING_RATES } from "constants/action-types";
import { formatMessage } from "lib/globalize-util";
import type { IAction } from "epics/base-epic";
import type { IStore } from "reducers";
import { updateWorkingOrderState } from "actions/working-order-state";
import type { IReviewTabStateAction } from "actions/working-order-state";
import type { IShippingRatesPayload, IShippingRatesResponseEntry } from "types/shipping";

export const fetchShippingRatesEpic = progressEpic<IReviewTabStateAction["payload"], IShippingRatesPayload>(
    SHIPPING_RATES.FETCH,
    ({ payload }) => ({
        method: "POST",
        url: siteConfig.api + "/api/v3.0/shipping/rates",
        body: JSON.stringify(payload),
    }),
    {
        checkError: (error) =>
            error?.status === 400
                ? Observable.of({
                      response: [],
                      status: 200,
                      xhr: error.xhr,
                  })
                : Observable.throw(error),
        dataCustomizer: (
            responseEntries: IShippingRatesResponseEntry[],
            action: IAction<IReviewTabStateAction["payload"]>,
            store: IStore,
            xhr
        ) => {
            const ratesBySplit = _.map(
                getSplitKeyLabels(responseEntries, store.scope),
                (splitRates, index, { length }) => ({
                    ...formatRatesStatus(splitRates, action.payload, store.scope, action.meta),
                    deliveryLabel: action.payload.order.order_number + (length > 1 ? `-${index + 1}` : ""),
                })
            );

            let errorMessage = xhr.response?.args?.errors?.[0]?.message || xhr.response?.message;
            if (!errorMessage && action.meta.isDropShip && _.every(ratesBySplit, ({ rates }) => _.isEmpty(rates))) {
                errorMessage = ratesBySplit?.[0]?.errors?.[0]?.message || formatMessage("Invalid destination address");
            }
            // store previous ratesBySplit in case of error, we'll fallback to previous address
            return errorMessage
                ? { ...store.shippingRates.payload, error: new APIError(errorMessage, 400, null) }
                : { ...store.shippingRates.payload, ratesBySplit, error: null };
        },
        finishActions: (__, action) => [updateWorkingOrderState({ shippingRates: true }, action.meta)],
        errorActions: (err, action: IAction<IReviewTabStateAction["payload"]>, store) => [
            {
                type: SHIPPING_RATES.FETCH.SUCCESS,
                payload: {
                    ...store.shippingRates.payload,
                    error: new APIError(
                        formatMessage("Shipping services are temporarily unavailable, please refresh and try again."),
                        err?.status || 500,
                        null
                    ),
                },
                meta: action.meta,
            },
        ],
    }
);
